import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterNewlands() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-newlands",
    name: "Painters Newlands",
    location: "Newlands, Cape Town",
    rating: 4.8,
    reviews: 173,
    specialties: ["Interior", "Exterior", "Heritage Homes", "Modern Properties"],
    image: "https://public.readdy.ai/ai/img_res/4879396f008ec8ad7934f3f5af63c8e7.jpg",
    verified: true,
    yearsExperience: 13,
    website: "https://paintersnewlands.co.za",
    email: "info@paintersnewlands.co.za",
    description: "Expert painting services in Newlands and southern suburbs. Specializing in both heritage home restoration and modern property painting with attention to architectural details.",
    services: [
      "Interior House Painting",
      "Exterior House Painting", 
      "Heritage Home Restoration",
      "Victorian Property Painting",
      "Modern Home Painting",
      "Color Consultation & Design",
      "Wallpaper Removal & Installation",
      "Decorative Finishes"
    ],
    serviceAreas: ["Newlands", "Claremont", "Rondebosch", "Kenilworth", "Southern Suburbs", "Cape Town", "Western Cape"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20heritage%20home%20interior%20painted%20by%20professional%20painters%20in%20Newlands%2C%20Victorian%20architecture%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20mountain%20views%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=newlands-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20heritage%20house%20painting%20project%20in%20Newlands%20Cape%20Town%2C%20Victorian%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20leafy%20suburban%20setting%2C%20well-maintained%20property&width=400&height=300&seq=newlands-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Modern%20home%20painting%20project%20in%20Newlands%2C%20professional%20painters%20working%20on%20contemporary%20property%20exterior%2C%20quality%20residential%20paint%20finish%2C%20mountain%20backdrop%2C%20organized%20work%20site&width=400&height=300&seq=newlands-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Living%20room%20interior%20painting%20in%20Newlands%20heritage%20home%2C%20fresh%20painted%20walls%2C%20period%20features%20preserved%2C%20quality%20paint%20finish%2C%20bright%20elegant%20space%2C%20professional%20painting%20work&width=400&height=300&seq=newlands-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Margaret Thompson",
        rating: 5,
        comment: "Exceptional work on our Victorian home in Newlands. The painters understood the heritage requirements and delivered a beautiful restoration that honors the original character."
      },
      {
        name: "James Wilson", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our Newlands home painting on schedule and the quality exceeded our expectations."
      },
      {
        name: "Sarah van Niekerk",
        rating: 4,
        comment: "Great attention to detail and very respectful of our heritage property. The color consultation was particularly helpful in choosing period-appropriate colors."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Newlands | Professional House Painting Services Cape Town | Residential Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Newlands offers professional painting services in Newlands, Cape Town. Specializing in residential painting, heritage properties, interior and exterior work. Quality local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Newlands, house painters Newlands, painting contractors Cape Town, residential painting, heritage property painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20elegant%20heritage%20home%20in%20Newlands%20Cape%20Town%2C%20leafy%20suburban%20setting%20with%20Table%20Mountain%20views%2C%20painters%20in%20clean%20uniforms%20with%20quality%20brushes%20and%20equipment%2C%20well-maintained%20Victorian%20and%20modern%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20project&width=1200&height=600&seq=newlands-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Newlands - Your Trusted Southern Suburbs Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 13 years of experience serving the prestigious southern suburbs of Cape Town, we have established ourselves as the premier choice for discerning homeowners who demand excellence in both craftsmanship and service.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Heritage Home Restoration Experts</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Newlands is renowned for its magnificent heritage homes, including stunning Victorian and Edwardian properties that require specialized restoration techniques. Our team has extensive experience working with period properties, understanding the unique challenges of maintaining historical integrity while incorporating modern paint technologies. We work closely with heritage consultants and local authorities to ensure all restoration work meets conservation standards while enhancing the property's original character and charm.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Modern Property Excellence</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  While we excel in heritage restoration, our expertise extends to contemporary homes throughout Newlands and the southern suburbs. Modern properties require different approaches, utilizing cutting-edge paint technologies and contemporary color palettes that complement architectural styles. Our team stays current with the latest trends in interior design and exterior finishes, ensuring your modern home reflects current aesthetic preferences while maintaining timeless appeal.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Southern Suburbs Climate Considerations</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The southern suburbs of Cape Town experience unique climatic conditions, with the area's proximity to Table Mountain creating specific weather patterns that affect paint performance. Our extensive local experience has taught us how to select and apply paints that withstand the region's variable weather conditions, from the strong south-easter winds to the occasional mountain mists. We use premium paint systems specifically chosen for their durability in the southern suburbs' microclimate.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Professional Color Consultation Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Choosing the right colors for your Newlands home requires understanding both the property's architectural style and its relationship to the surrounding landscape. Our professional color consultants work with you to select palettes that enhance your home's character while complementing the leafy, established neighborhood aesthetic. For heritage properties, we research period-appropriate color schemes, while for modern homes, we explore contemporary options that reflect your personal style.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Materials and Sustainable Practices</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  We exclusively use premium paint brands known for their durability, coverage, and environmental responsibility. Our commitment to sustainability includes using low-VOC and eco-friendly paint options wherever possible, ensuring your home's indoor air quality remains healthy while minimizing environmental impact. All our materials are sourced from reputable suppliers, and we maintain strict quality control standards throughout every project.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20heritage%20home%20in%20Newlands%20Cape%20Town%2C%20southern%20suburbs%20property%20with%20mountain%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20traditional%20Victorian%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=newlands-sidebar-image&orientation=landscape"
                      alt="Professional painters Newlands heritage house painting services Cape Town southern suburbs Victorian"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Painting Services in Newlands and Southern Suburbs</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Heritage and Modern Property Painting</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our comprehensive painting services cater to both the magnificent heritage homes and contemporary properties that define the Newlands area. Whether you own a Victorian mansion requiring careful restoration or a modern home needing a fresh update, our experienced team delivers exceptional results that enhance your property's value and aesthetic appeal.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with premium materials and expert application techniques for Newlands properties.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Interior Painting Excellence</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our interior painting services transform Newlands homes into beautiful, comfortable living spaces that reflect the area's sophisticated character. We understand that many properties in this prestigious suburb feature unique architectural details, high ceilings, and period features that require specialized techniques and careful attention to detail.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    For heritage properties, we use traditional techniques combined with modern paint technologies to preserve original features while providing long-lasting protection. Our team is experienced in working around valuable antiques, artwork, and period fixtures, ensuring your possessions are protected throughout the painting process.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exterior Restoration and Protection</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Exterior painting in Newlands requires understanding of both heritage conservation principles and modern protective technologies. Our comprehensive exterior painting process includes thorough surface preparation, appropriate primer selection, and multiple coats of premium paint designed to withstand the southern suburbs' climate conditions.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We specialize in restoring original architectural features such

                    as decorative moldings, window frames, and period details, using techniques that honor the craftsmanship of the original builders while providing modern protection against weather and environmental factors.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Painting Projects in Newlands</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Newlands and the southern suburbs. Each project demonstrates our commitment to quality workmanship and our expertise in both heritage restoration and modern property enhancement.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Newlands Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional heritage and modern property painting with premium materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Newlands and the southern suburbs have to say about our painting services. Our commitment to excellence and customer satisfaction is reflected in these genuine reviews from homeowners in the area.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Newlands</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to transform your Newlands property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, long-lasting results that enhance your southern suburbs lifestyle.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding southern suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Quote Today</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation quote for your Newlands painting project. Our experienced team will assess your property and provide detailed recommendations tailored to heritage or modern requirements.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
